import 'font-awesome/css/font-awesome.css';
import 'bootstrap/dist/css/bootstrap.css';
import './App.css';
import './components/Common/ToolBarSearch.scss';
import React from 'react';

import { ReduxRouter } from '@lagunovsky/redux-react-router';
import { AppInsightsErrorBoundary } from '@microsoft/applicationinsights-react-js';
import { createBrowserHistory } from 'history';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { map, Subject, switchMap, takeUntil, filter, first } from 'rxjs';

import App from 'App';
import { accessToken$ } from 'subjects/common/CurrentUserService';
import { configurationService } from 'subjects/crossModules/ConfigurationService';

import { getAppInsights, reactPlugin } from './ApplicationInsights';
import auth from './Auth';
import AuthProvider, { AuthProviderVariables } from './AuthProvider';
import { unregister } from './registerServiceWorker';
import configureStore from './store/configureStore';

const LOCALSTORAGE_RECORD_SERVER_TIMING_KEY = 'recordServerTiming';
const sessionRecord = localStorage.getItem(LOCALSTORAGE_RECORD_SERVER_TIMING_KEY) || 'false';
let record = sessionRecord === 'true';

const onPageDestroy$ = new Subject<void>();
window.addEventListener('beforeunload', () => {
    onPageDestroy$.next();
    onPageDestroy$.complete();
});

accessToken$
    .pipe(
        filter(Boolean),
        first(),
        takeUntil(onPageDestroy$),
        switchMap(() =>
            configurationService.getUiFeatureFlags$().pipe(
                map((response) => {
                    const flags = response.response;
                    if (!flags) {
                        return false;
                    }

                    return flags?.recordServerTiming || false;
                }),
            ),
        ),
    )
    .subscribe({
        next: (value) => {
            record = value;
            localStorage.setItem(LOCALSTORAGE_RECORD_SERVER_TIMING_KEY, Boolean(value).toString());
        },
        error: console.error,
    });

(function () {
    // Save a reference to the original open and send methods
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url, async, user, password) {
        this._url = url;
        originalOpen.call(this, method, url, async ?? true, user, password);
    } as unknown as typeof XMLHttpRequest.prototype.open;

    XMLHttpRequest.prototype.send = function (body) {
        if (this._url.includes('api/') && record) {
            this.addEventListener('readystatechange', () => {
                if (this.readyState === 4) {
                    const entries = performance
                        .getEntriesByType('resource')
                        .filter((entry) => entry.name === this.responseURL);
                    if (entries.length) {
                        const entry = entries[0] as PerformanceResourceTiming;
                        getAppInsights().trackEvent({
                            name: 'UIResourceTiming',
                            properties: {
                                url: this._url,
                                duration: entry.duration,
                                startTime: entry.startTime,
                                user: auth.currentUser()?.emails?.[0],
                                dnsLookupTime: entry.domainLookupEnd - entry.domainLookupStart,
                                tcpConnectTime: entry.connectEnd - entry.connectStart,
                                responseTime: entry.responseEnd - entry.responseStart,
                                serverWaitTime: entry.responseStart - entry.requestStart,
                                serverTiming: this.getResponseHeader('Server-Timing'),
                                appInsightsCorrelationId: this.getResponseHeader('X-Request-Id'),
                            },
                        });
                    }
                }
            });
        }
        originalSend.call(this, body);
    };
})();

// Create browser history to use in the Redux store
const baseUrl = document.getElementsByTagName('base')[0].getAttribute('href');
const history = createBrowserHistory({ basename: baseUrl } as any);

// Get the application-wide store instance, prepopulating with state from the server where available.
const initialState = (window as any).initialReduxState;
const store = configureStore(history, initialState);

const container = document.getElementById('root');
const root = createRoot(container as HTMLElement);

const getUrlVars = () => {
    const vars = {};
    window.location.href.replace(/[?&]+([^=&]+)=([^&]*)/gi, function (m, key, value): any {
        vars[key] = value;
    });
    return vars;
};
const urlAccessToken = getUrlVars()['cc_access_token'];

const StrictModeWrapper = ({ children }) => {
    const isLocalhost = window.location.hostname === 'localhost';
    return isLocalhost ? <React.StrictMode>{children}</React.StrictMode> : <>{children}</>;
};

const renderContent = () => (
    <StrictModeWrapper>
        <Provider store={store}>
            <ReduxRouter
                history={history}
                store={store}
            >
                <AppInsightsErrorBoundary
                    onError={() => <h1>something went wrong</h1>}
                    appInsights={reactPlugin}
                >
                    <App />
                </AppInsightsErrorBoundary>
            </ReduxRouter>
        </Provider>
    </StrictModeWrapper>
);

if (urlAccessToken) {
    const appInsights = getAppInsights();
    appInsights.trackEvent({ name: 'AVA-Member-UI-Login-UrlAccessToken' });
    sessionStorage.setItem(AuthProviderVariables.CC_ACCESS_TOKEN, urlAccessToken);
    root.render(renderContent());
    unregister();
} else {
    root.render(<AuthProvider>{renderContent()}</AuthProvider>);
    unregister();
}
